import React, { useEffect, useState, useRef } from 'react';
import { Typography, Box, useMediaQuery, useTheme, CircularProgress, Button, FormControl } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { MTable, Select } from '../../../../../Components';
import { SelectOption } from '../../../../../Components/Form/Form.types';
import { StepButtonBar } from '../OtherRequirementWizard/StepButtonBar';
import { StepProps } from './types';
import { useAptorApi } from '../../../../../Api';
import UpdateConnectionInfo from './UpdateConnectionInfo';

interface Step6LawConnectionAddFormProps extends StepProps {
  lawId?: number;
  unitId?: number;
  selectedConnectionType?: SelectOption | null;
  localAddedItems?: any[];
  onAddItem: (item: any) => void;
  onRemoveAddedItem: (index: number) => void;
  currentStepName: string;
  updateConnectionInfo: any;
  setUpdateConnectionInfo: (info: any) => void;
}

const Step6LawConnectionAddForm = ({
  onNext,
  onBack,
  onCancel,
  isFirstStep,
  lawId,
  unitId,
  selectedConnectionType,
  localAddedItems = [],
  onAddItem,
  onRemoveAddedItem,
  currentStepName,
    updateConnectionInfo,
  setUpdateConnectionInfo,
}: Step6LawConnectionAddFormProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { api, abortController } = useAptorApi();
  const [availableItems, setAvailableItems] = useState<SelectOption[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<any>(null);
  const tableRef = useRef<any>(null);
const paperPadding = isMobile ? theme.spacing(3) : theme.spacing(8);
  useEffect(() => {
    if (lawId && unitId) {
      fetchAvailableItems();
    }
  }, [lawId, unitId, selectedConnectionType]);

  const fetchAvailableItems = async () => {
    setIsLoadingItems(true);
    try {
      let items: any[] = [];

      if (selectedConnectionType?.value === 'process') {
        const result = await api.getCustomerRequirementProcesses(lawId!, unitId!);
        items = result?.items || [];
      } else if (selectedConnectionType?.value === 'aspect') {
        const result = await (api as any).getComanyUnitRequirementAspects(lawId!, unitId!);
        items = result?.items || [];
      } else if (selectedConnectionType?.value === 'tag') {
        const result = await api.getCustomerRequirementTags(lawId!, unitId!);
        items = result?.items || [];
      }

      if (!abortController.current.signal.aborted) {
        const options = items.map((item: any) => ({ label: item.name, value: item.id }));
        setAvailableItems(options);
        setIsLoadingItems(false);
      }
    } catch (error) {
      if (!abortController.current.signal.aborted) {
        console.error('Failed to fetch available items:', error);
        setAvailableItems([]);
        setIsLoadingItems(false);
      }
    }
  };

  const getTitle = () => {
    switch (selectedConnectionType?.value) {
      case 'process':
        return formatMessage({ id: 'law-portal.manage.edit-law.connectedProcesses' });
      case 'aspect':
        return formatMessage({ id: 'law-portal.manage.edit-law.connectedAspects' });
      case 'tag':
        return formatMessage({ id: 'law-portal.manage.edit-law.connectedTags' });
      default:
        return '';
    }
  };

  const getFilteredItems = () => {
    const addedIds = localAddedItems.map((item) => item.id);
    return availableItems.filter((opt) => !addedIds.includes(opt.value));
  };

  const handleAddItem = () => {
    if (!selectedItemId) return;

    const selectedItem = availableItems.find((opt) => opt.value === selectedItemId);
    if (selectedItem) {
      onAddItem({ id: selectedItem.value, name: selectedItem.label });
      setSelectedItemId(null);
    }
  };

  const handleDeleteItem = (index: number) => {
    onRemoveAddedItem(index);
  };

  const deleteAction = (row: any) => ({
    tooltip: formatMessage({ id: 'component.mtable.deleteTooltip' }),
    icon: 'delete',
    onClick: () => {
      const index = localAddedItems.findIndex((item) => item.id === row.id);
      if (index !== -1) {
        handleDeleteItem(index);
      }
    },
  });

  return (
      <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: isMobile ? '100%' : 'calc(50% - 5px)',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            // justifyContent: 'center',
            // alignItems: 'center',
            minHeight: '350px',
          }}
        >
          <Box style={{ width: '100%' }}>
          <Typography variant="body2" style={{ marginBottom: theme.spacing(2), fontWeight: 600 }}>
          {formatMessage({ id: 'form.add', defaultMessage: 'Add' })} - {getTitle()}
        </Typography>
        {isLoadingItems ? (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(4) }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box style={{ display: 'flex', gap: theme.spacing(1), marginBottom: theme.spacing(2), flexWrap: 'wrap',width: '100%' }}>
              <FormControl style={{ flex: 1, maxWidth: '250px' }}>
                <Select
                  selectProps={{
                    options: getFilteredItems(),
                    value: selectedItemId ? { label: availableItems.find(o => o.value === selectedItemId)?.label || '', value: selectedItemId } : null,
                    onChange: (val: any) => setSelectedItemId(val?.value || null),
                    placeholder: getTitle(),
                    isClearable: true,
                  }}
                />
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddItem}
                disabled={!selectedItemId}
                style={{ alignSelf: 'flex-end', textTransform: 'none' }}
              >
                {formatMessage({ id: 'form.add' })}
              </Button>
            </Box>

            <Box style={{ maxHeight: '400px', overflow: 'auto',width: '100%' }}>

              <MTable
                  key={`add-table-${localAddedItems.length}`}
                  tableRef={tableRef}
                  columns={[{ title: formatMessage({ id: 'form.name' }), field: 'name' }]}
                  actions={[deleteAction]}
                  data={localAddedItems}
                  components={{}}
                />
            </Box>
          </>
        )}
          </Box>
        </div>
        <UpdateConnectionInfo
          updateConnectionInfo={updateConnectionInfo}
          currentStepName={currentStepName}
          onUpdate={setUpdateConnectionInfo}
        />
      </div>

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={onNext}
          nextLabel="form.next"
          nextDisabled={localAddedItems.length === 0}
          formatMessage={formatMessage}
          showBack={!isFirstStep}
        />
      </Box>
    </div>
  );
};

export default Step6LawConnectionAddForm;
